import { Box, Grid, Typography } from '@material-ui/core';
import React from 'react';
import { useSelector } from 'react-redux';
import { ScaleStatus } from '../../../devices/device-enums';
import VerticalCenter from '../../../vertical-center/vertical-center';
import { AppState } from '../../redux/configure-store';
import {
  getMaxWeight,
  getMinWeight,
  getNofDivision,
  getRepeatability,
  getWeightUnitFromJPOSWeightUnit,
} from './helper/get-weight-unit';
import { getTemperatureUnit } from './helper/get-temperature-unit';
import { styles } from './live-weight-area-style';
import {
  SvgLiveWeightStatusIconConnected,
  SvgLiveWeightStatusIconNotReady,
  SvgLiveWeightStatusIconOverweight,
  SvgLiveWeightStatusIconUnderweight,
  SvgLiveWeightStatusIconUnstable,
} from './live-weight-status-icons';
import { ScaleClass3 } from './media';

interface UOMInterface {
  code?: string;
  precision?: number;
}

export const getLiveWeightStatus = (
  status: ScaleStatus,
  value: number,
  UOM?: UOMInterface,
): JSX.Element | string => {
  const zero = 0;
  switch (status) {
    case ScaleStatus.NOT_READY:
      return <SvgLiveWeightStatusIconNotReady data-testid='live-weight-status-icon-not-ready' />;
    case ScaleStatus.STABLE_WEIGHT:
      return `${Number(value).toFixed(UOM?.precision)} ${UOM?.code}`;
    case ScaleStatus.WEIGHT_OVERWEIGHT:
      return <SvgLiveWeightStatusIconOverweight />;
    case ScaleStatus.WEIGHT_UNDER_ZERO:
      return <SvgLiveWeightStatusIconUnderweight />;
    case ScaleStatus.WEIGHT_UNSTABLE:
      return <SvgLiveWeightStatusIconUnstable />;
    case ScaleStatus.CONNECTED:
      return <SvgLiveWeightStatusIconConnected uom={UOM?.code} />;
    case ScaleStatus.WEIGHT_ZERO:
      return `${zero.toFixed(UOM?.precision)} ${UOM?.code}`;
    //TODO check with PO about the unknown general error.(Also see in legacy how it's done!)
    case ScaleStatus.UNKNOWN_STATUS:
      return <SvgLiveWeightStatusIconNotReady />;
  }
};

export const LiveWeightArea: React.FC<LiveWeightArea> = (props: LiveWeightArea): JSX.Element => {
  const classes = styles();
  const { isLeftHanded, isSummaryDisabled, isReversed } = props;
  const scaleDeviceStatus = useSelector((state: AppState) =>
    state.deviceStatus.devices.filter(d => d.deviceType === 'Scale'),
  );
  const scaleData = useSelector((state: AppState) => state.scale);
  const UOMPrecision = useSelector(
    (state: AppState) =>
      state.touchPoint.touchPointConfiguration.defaultWeightUnitOfMeasurePrecision,
  );
  const weightUnit = getWeightUnitFromJPOSWeightUnit(scaleData.weightUnit);
  if (scaleDeviceStatus.length === 0) {
    return <></>;
  }

  const maxWeightFromDeviceConfig = scaleDeviceStatus[0]?.maxWeight;
  const minWeightFromDeviceConfig = scaleDeviceStatus[0]?.minWeight;
  const maxTemperatureFromDeviceConfig = getTemperatureUnit(
    scaleDeviceStatus[0]?.maxTemperature || '',
  );
  const minTemperatureFromDeviceConfig = getTemperatureUnit(
    scaleDeviceStatus[0]?.minTemperature || '',
  );
  const UOM = { code: weightUnit, precision: UOMPrecision };
  // ToDo: Temporary band-aid to be fixed in XLR-2236
  const getStatus = (): ScaleStatus => {
    if (isReversed) {
      // For Login/Registration view only
      if (ScaleStatus.NOT_READY === scaleData.status) {
        return ScaleStatus.NOT_READY;
      }
      return ScaleStatus.CONNECTED;
    }
    return scaleData.status;
  };

  const liveWeightParams = {
    status: getStatus(),
    weight: scaleData.weight,
  };

  const getTemperatureMinMaxDisplay = (): JSX.Element => {
    if (minTemperatureFromDeviceConfig && maxTemperatureFromDeviceConfig) {
      return (
        <>
          <br />
          {minTemperatureFromDeviceConfig} to {maxTemperatureFromDeviceConfig}
        </>
      );
    }
    return <></>;
  };

  const layout = [
    <Grid
      key={'liveWeight'}
      item
      className={isLeftHanded ? classes.weightItemInverse : classes.weightItem}
      data-testid='live-weight-scale-status'
    >
      <VerticalCenter justifyContent='flex-start'>
        <Box
          data-testid='live-weight-area-weight'
          className={`${classes.weight} ${isSummaryDisabled ? classes.disabled : ''}`}
        >
          <Typography variant='h3'>
            {getLiveWeightStatus(liveWeightParams.status, liveWeightParams.weight, UOM)}
          </Typography>
        </Box>
      </VerticalCenter>
    </Grid>,
    <Grid
      item
      container
      key={'maxDimensions'}
      className={classes.iconAndTextContainer}
      data-testid='live-weight-scale-default'
      wrap='nowrap'
    >
      <Grid container className='fullHeight' justifyContent='flex-start'>
        <Grid
          container
          alignItems='center'
          data-testid='live-weight-area-default-values'
          className={`${classes.readme} ${isSummaryDisabled ? classes.disabled : ''}`}
        >
          <Typography variant='caption' className={classes.adjustOverflow}>
            e=d={getRepeatability(UOMPrecision, weightUnit)}
            {weightUnit} Max {maxWeightFromDeviceConfig ?? getMaxWeight(scaleData.maxWeight)}
            {weightUnit}
            <br />
            Min {minWeightFromDeviceConfig ?? getMinWeight(UOMPrecision, weightUnit)}
            {weightUnit} n_Max{' '}
            {getNofDivision(
              UOMPrecision,
              weightUnit,
              maxWeightFromDeviceConfig ? Number(maxWeightFromDeviceConfig) : scaleData.maxWeight,
            )}{' '}
            {getTemperatureMinMaxDisplay()}
          </Typography>
        </Grid>
      </Grid>
      <Grid
        className={classes.scaleIconContainer}
        justifyContent='center'
        item
        container
        direction='column'
      >
        <ScaleClass3 className={classes.class3Icon} />
      </Grid>
    </Grid>,
  ];

  const returnLayout = (): JSX.Element[] => {
    if (isReversed) {
      return layout.reverse().map(l => l);
    } else {
      return layout.map(l => l);
    }
  };

  return <>{returnLayout()}</>;
};

interface LiveWeightArea {
  isLeftHanded: boolean;
  isSummaryDisabled: boolean;
  isReversed?: boolean;
}
